// Ported from qwick-business-app @ 1cc869ea (src/features/ai/domains/drafts/store/use-draft-store.ts, to-wire-draft.ts).
import type { ShiftDraftV1 } from "@gigpro/chat-views";
import { atomWithStorage } from "jotai/utils";
import { safeJSONStorage } from "utils/safeStorage";

/** `unconfirmed`: a post a reload interrupted, which may have gone through, so it is never postable again. */
type DraftStatus = "draft" | "posting" | "posted" | "failed" | "unconfirmed";

export type DraftEntry = {
  data: ShiftDraftV1;
  status: DraftStatus;
  /** The created gig once posted; rides back to the agent so it can act on the shift without searching. */
  gigId: number | null;
  /** The conversation that drafted it; absent on entries stored before this was recorded. */
  conversationId?: string | null;
  error?: string;
  /** The manager edited this draft on its card; the agent re-sending the same revision must not undo that. */
  edited?: boolean;
  /** Posted, but the shift goes live only once Qwick approves its location. */
  pendingApproval?: boolean;
  updatedAt: number;
};

export type DraftEntries = Record<string, DraftEntry>;

// Every entry rides in every turn's activeDrafts, so the record is capped and aged out.
const MAX_ENTRIES = 20;
const MAX_AGE_MS = 48 * 60 * 60 * 1000;

const prune = (entries: DraftEntries, now: number): DraftEntries =>
  Object.fromEntries(
    Object.entries(entries)
      .filter(([, entry]) => now - entry.updatedAt <= MAX_AGE_MS)
      .toSorted(([, a], [, b]) => b.updatedAt - a.updatedAt)
      .slice(0, MAX_ENTRIES),
  );

export const upsertDraft = (
  entries: DraftEntries,
  data: ShiftDraftV1,
  now: number,
  conversationId?: string | null,
): DraftEntries => {
  const existing = entries[data.draft_id];
  const revision = data.revision ?? 1;
  const existingRevision = existing?.data.revision ?? 1;
  if (existing && revision < existingRevision) return entries;
  // The same revision re-renders on scroll and history replay; it must not make a posted draft look unposted.
  if (existing && revision === existingRevision) {
    return {
      ...entries,
      [data.draft_id]: {
        ...existing,
        data: existing.edited ? existing.data : data,
        conversationId: existing.conversationId ?? conversationId,
        updatedAt: now,
      },
    };
  }
  // A post in flight or done keeps the details it posted with: showing a later revision would misreport the shift.
  if (existing?.status === "posting" || existing?.status === "posted") {
    return { ...entries, [data.draft_id]: { ...existing, updatedAt: now } };
  }
  // A revision of a post that may already exist must not offer Post again.
  const status = existing?.status === "unconfirmed" ? "unconfirmed" : "draft";
  return prune({ ...entries, [data.draft_id]: { data, status, gigId: null, conversationId, updatedAt: now } }, now);
};

/** A card edit, kept at the agent's revision so the next turn reports it as the same draft. */
export const editDraft = (
  entries: DraftEntries,
  data: ShiftDraftV1,
  now: number,
  conversationId?: string | null,
): DraftEntries => {
  const existing = entries[data.draft_id];
  return {
    ...entries,
    [data.draft_id]: {
      ...(existing ?? { status: "draft", gigId: null }),
      data,
      edited: true,
      conversationId: existing?.conversationId ?? conversationId,
      updatedAt: now,
    },
  };
};

/** Marks a post in flight, adding the draft first if no turn has recorded it yet. */
export const beginPosting = (
  entries: DraftEntries,
  data: ShiftDraftV1,
  now: number,
  conversationId?: string | null,
): DraftEntries => {
  const known = entries[data.draft_id] ? entries : upsertDraft(entries, data, now, conversationId);
  const withStatus = setDraftStatus(known, data.draft_id, "posting", now);
  const entry = withStatus[data.draft_id];
  // A draft stored before conversations were recorded is adopted by the conversation that posts it.
  if (!entry) return withStatus;
  const { error: _previous, ...attempt } = entry;
  return {
    ...withStatus,
    [data.draft_id]: entry.conversationId == null && conversationId ? { ...attempt, conversationId } : attempt,
  };
};

export const setDraftStatus = (
  entries: DraftEntries,
  draftId: string,
  status: DraftStatus,
  now: number,
  error?: string,
): DraftEntries => {
  const existing = entries[draftId];
  if (!existing) return entries;
  return { ...entries, [draftId]: { ...existing, status, updatedAt: now, ...(error === undefined ? {} : { error }) } };
};

export const markDraftPosted = (
  entries: DraftEntries,
  draftId: string,
  gigId: number,
  now: number,
  pendingApproval = false,
): DraftEntries => {
  const existing = entries[draftId];
  if (!existing) return entries;
  const { error: _cleared, ...rest } = existing;
  return {
    ...entries,
    [draftId]: { ...rest, status: "posted", gigId, updatedAt: now, ...(pendingApproval && { pendingApproval }) },
  };
};

/** Applied when read from storage: age out stale drafts, and a post a reload interrupted becomes unconfirmed. */
export const rehydrateDrafts = (persisted: unknown, now: number): DraftEntries => {
  if (typeof persisted !== "object" || persisted === null) return {};
  const entries = Object.fromEntries(
    Object.entries(persisted as DraftEntries).map(([id, entry]) => [
      id,
      entry.status === "posting" ? { ...entry, status: "unconfirmed" as const } : entry,
    ]),
  );
  return prune(entries, now);
};

/** A shift the manager posted by hand on the page: the agent reads it as posted, so it never contradicts the page. */
export const recordHandPost = (
  entries: DraftEntries,
  posted: { gigId: number; data: ShiftDraftV1 },
  now: number,
  conversationId: string | null,
): DraftEntries =>
  prune(
    {
      ...entries,
      [posted.data.draft_id]: {
        data: posted.data,
        status: "posted",
        gigId: posted.gigId,
        conversationId,
        edited: true,
        updatedAt: now,
      },
    },
    now,
  );

/** A hand post whose gig was cancelled since: the agent must not read it as posted any more. */
export const dropCancelledHandPosts = (entries: DraftEntries, gigIds: ReadonlySet<string>): DraftEntries => {
  const kept = Object.entries(entries).filter(
    ([id, entry]) => !(id.startsWith("page-") && entry.gigId !== null && gigIds.has(String(entry.gigId))),
  );
  return kept.length === Object.keys(entries).length ? entries : Object.fromEntries(kept);
};

/** A request carries only this conversation's drafts at the selected location; a new chat carries none. */
export const draftsInScope = (
  entries: DraftEntries,
  scope: { conversationId: string | null; locationId: number | undefined },
): DraftEntries =>
  Object.fromEntries(
    Object.entries(entries).filter(
      ([, entry]) =>
        scope.conversationId !== null &&
        scope.locationId !== undefined &&
        entry.conversationId === scope.conversationId &&
        entry.data.location_id === String(scope.locationId),
    ),
  );

/** The app↔MCP contract: status is binary, and only a posted draft carries its gig id. */
export const toWireDrafts = (entries: DraftEntries): Array<Record<string, unknown>> =>
  Object.values(entries).map(({ data, status, gigId }) => ({
    draft_id: data.draft_id,
    revision: data.revision ?? 1,
    position_id: data.position_id,
    position_label: data.position_label,
    location_id: data.location_id,
    location_label: data.location_label,
    starts_at: data.starts_at,
    ends_at: data.ends_at,
    pay_rate: data.pay_rate,
    spots: data.spots,
    ...(data.unpaid_break_minutes == null ? {} : { unpaid_break_minutes: data.unpaid_break_minutes }),
    notes: data.notes ?? "",
    auto_filled: data.auto_filled ?? [],
    suggested_actions: data.suggested_actions ?? [],
    ...(data.manager_on_duty == null ? {} : { manager_on_duty: data.manager_on_duty }),
    status: status === "posted" ? "posted" : "draft",
    gig_id: status === "posted" ? gigId : null,
  }));

// Keyed per user, so a second account on the same browser never sends the first one's drafts to the agent.
const draftAtoms = new Map<string, ReturnType<typeof makeDraftsAtom>>();

const jsonStorage = safeJSONStorage<DraftEntries>();
// Rehydrated only when read from storage: within a page, `posting` must stay visible to every card.
const draftStorage: typeof jsonStorage = {
  ...jsonStorage,
  getItem: (key, initialValue) => rehydrateDrafts(jsonStorage.getItem(key, initialValue), Date.now()),
};

const makeDraftsAtom = (userId: string) =>
  atomWithStorage<DraftEntries>(`qwick-ai-chat-drafts-${userId}`, {}, draftStorage, { getOnInit: true });

export const draftsAtomFor = (userId: string) => {
  const existing = draftAtoms.get(userId);
  if (existing) return existing;
  const created = makeDraftsAtom(userId);
  draftAtoms.set(userId, created);
  return created;
};
